import type { Module } from 'vuex';
import { ICategory } from '../../entities/category-entity';
import {IMenuTreeItem} from "../../entities/menu-entity";
import {Category} from "sitisjs/dist/internal/sections/categories/models/Category";

export const namespace = 'catalog';

export interface ICatalogState {
	currentCategory: null | ICategory;
	error: unknown;
	categories: ICategory[];
}
export interface IGetCategoryDetailedParams {
	slug: string;
	extensions?: string[];
}

export const MutationType = {
	SET_CATEGORY: 'setCategory',
	SET_CATEGORY_DETAILED: 'setCategoryDetailed',
	SET_CATEGORIES: 'setCategories',
	SET_ERROR: 'setError',
};

export const actionType = {
	GET_CATALOG: 'getCatalog',
	GET_CATEGORY: 'GET_CATEGORY',
	GET_CATEGORY_DETAILED: 'GET_CATEGORY_DETAILED',
};

function makeTree(nodes: any[], pid: number | null = null): any[] {
	return nodes
		.filter((n) => n.parentId === pid)
		.reduce(
			(tree, n) => [
				...tree,
				{
					...n,
					items: makeTree(nodes, n.id),
				},
			],
			[],
		);
}


// Получение родителей категории
const getCategoryParent = (parentId: number, cateogries: ICategory[]): ICategory | undefined => cateogries.find((c) => c.id === parentId);
const getCategoryParents = (cateogry: ICategory, cateogries: ICategory[], parents: ICategory[]): ICategory[] => {
	if (parents.length === 0) {
		parents.push(cateogry);
	}
	if (cateogry.parentId) {
		const parent = getCategoryParent(cateogry.parentId, cateogries);
		if (parent) {
			parents.push(parent);
			return getCategoryParents(parent, cateogries, parents);
		}
	}
	return parents;
};
const findCategoryParent = (currentCategory: ICategory | null, categories: ICategory[]) => {

	if ( !currentCategory?.parentId ) {
		return undefined
	}

	return (categories || []).find((t) => t.id === currentCategory?.parentId);
}
// -----------------------------

// Получение детей категории
const getCategoryChildren = (currentCategory: ICategory | null, catalog: IMenuTreeItem[]) => {
	for (let t of catalog) {
		if (t.id == currentCategory?.id) {
			if (t.items.length > 0) {
				return t.items;
			} else {
				return catalog;
			}
		} else if (t.items.length > 0) {
			let currentResult: any = getCategoryChildren(currentCategory, t.items);
			if (currentResult.length > 0) {
				return currentResult;
			}
		}
	}
	return [];
}
const getCategoryChildrens = (currentCategory: ICategory | null, categories: ICategory[]): IMenuTreeItem[] => {
	const catalogTree = makeTree(categories, null);

	return getCategoryChildren(currentCategory, catalogTree);
}
// -------------------------



export default function catalogStore(): Module<ICatalogState, any> {
	return {
		namespaced: true,
		state: () => ({
			currentCategory: null,
			error: null,
			categories: [],
		}),
		actions: {
			async [actionType.GET_CATALOG]({ commit }): Promise<ICategory> {
				try {
					commit('appState/setLoading', { catalog: true }, { root: true });
					const res = await this.$api.category.getCategories.execute({
						perPage: 9999,
					});
					const catalog = res.getValue();
					const categories = catalog?.categories || [];
					commit(MutationType.SET_CATEGORIES, categories);
					return categories;
				} finally {
					commit('appState/setLoading', { catalog: false }, { root: true });
				}
			},
			async [actionType.GET_CATEGORY]({ commit, state, dispatch }, slug: string): Promise<ICategory | undefined> {
				try {
					commit('appState/setLoading', { category: true }, { root: true });
					if (state.categories.length) {
						const category = state.categories.find((c: ICategory) => c.slug === slug);
						// commit(MutationType.SET_CATEGORY, category || null);
						return category;
					}
					await dispatch(actionType.GET_CATALOG);
					const category = await dispatch(actionType.GET_CATEGORY, slug);
					return category;
				} finally {
					commit('appState/setLoading', { category: false }, { root: true });
				}
			},
			async [actionType.GET_CATEGORY_DETAILED]({ commit, state, dispatch }, params: IGetCategoryDetailedParams): Promise<ICategory | undefined | any> {
				const {
					slug,
					extensions
				} = params;

				let filters = [];
				if ((extensions || []).length > 0) {
					filters.push(`expand=${extensions?.join(',')}`)
				}

				try {
					commit('appState/setLoading', { category: true }, { root: true });

					const filterString = Boolean(filters.length > 0) ? `?${filters.join("&")}` : "";
					const response = await this.$api.agent.transport.get(`/categories/${ slug }${ filterString }`).then((res: any) => {
						return res.data
					}).catch((err:any) => {
						return {error: err.response}
					})
					if (!response || response.error) {
						const xRedirect: string = response?.error?.headers?.['x-redirect'];
						if (xRedirect) {
							return {
								xRedirect: xRedirect
							}
						}

						return undefined
					}

					const category = new Category(response);

					commit(MutationType.SET_CATEGORY_DETAILED, category || null);

					return category;
				} finally {
					// commit('appState/setLoading', { category: false }, { root: true });
				}
			},
		},
		mutations: {
			[MutationType.SET_CATEGORY]: (state, category: ICategory) => {
				state.currentCategory = category;
			},
			[MutationType.SET_CATEGORY_DETAILED]: (state, category: ICategory) => {
				state.currentCategory = category;
			},
			[MutationType.SET_CATEGORIES]: (state, categories: ICategory[]) => {
				state.categories = categories;
			},
			[MutationType.SET_ERROR]: (state, err: unknown) => {
				state.error = err;
			},
		},
		getters: {
			currentCategory: ({ currentCategory }) => currentCategory,
			breadcrumbs: ({ currentCategory, categories }): any[] => {
				const breadcrumbs: any[] = [{ title: 'Главная', path: '/' }];
				if (currentCategory) {
					let path = '';
					getCategoryParents(currentCategory, categories, [])
						.reverse()
						.forEach(({ name: title, slug }) => {
							path += `/${slug}`;
							breadcrumbs.push({ title, path });
						});
				}

				return breadcrumbs;
			},
			categories: ({ categories }): ICategory[] => categories,
			catalogTree: ({ categories }): any[] => makeTree(categories, null),
			categoryChildrens: ({ currentCategory, categories }): any[] => getCategoryChildrens(currentCategory, categories),
			categoryParent: ({ currentCategory, categories }): any => findCategoryParent(currentCategory, categories),
		},
	};
}
